import { useId } from "react";

/**
 * BrandMark (#1300, #1153, D421) — the ONE place the app draws the
 * CricketCompass mark. Every new-look screen and kit part renders this
 * component, and (D307) so does the old look's `CompassLogo`; no other file
 * under `src` draws the shape itself (`oneMark.test.js` guards that).
 *
 * Provenance: mark v2 (D421), the geometric redraw from the brand kit's
 * `scripts/build_mark_v2.py` (master `logos/svg/mark-on-light.svg`, 2026-10-28):
 * true circles, equal ring gaps, four ticks at 45 degrees, even streaks, a
 * clean gap around the ball and a two-line seam clipped to the ball. The
 * geometry below is the master's, verbatim, in its `0 0 1000 1000` viewBox.
 *
 * History: v1 was Martin's logo traced with potrace from #259 "New Dark
 * Logo.png" (D228.3 replaced D140's drawn kite; D313 Martin confirmed it; D307
 * put it everywhere as a recorded D217 exception limited to the artwork;
 * Q-B green is the brand guide section 4 #5D9D35; Q-D 24px in the bar). D421
 * replaced the trace with this geometry and kept the props and the box.
 *
 * The navy elements (ring, east/west points, C, seam) take `ring`: navy on a
 * light surface (`variant="light"`, the kit token, so it follows
 * `html.hc-theme`) and white on a navy one (`variant="dark"`). The green
 * elements are always `GREEN`. The seam clipPath id is per instance (`useId`)
 * because several marks can share a page.
 *
 * `data-brand-mark` carries the artwork's name; journey 71 reads it.
 */
const GREEN = "#5D9D35";

export function BrandMark({ size = 22, variant = "light", testId = "brand-mark" }) {
  const ring = variant === "dark" ? "#FFFFFF" : "var(--nw-navy)";
  const clipId = `cc-ball-${useId().replace(/:/g, "")}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1000 1000"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
      data-testid={testId}
      data-brand-mark="geometric-v2"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="536" cy="490" r="84" />
        </clipPath>
      </defs>
      <path fill={ring} d="M879.89,556 A384,384 0 0 1 556,879.89 L556,849.54 A354,354 0 0 0 849.54,556 Z M444,879.89 A384,384 0 0 1 120.11,556 L150.46,556 A354,354 0 0 0 444,849.54 Z M120.11,444 A384,384 0 0 1 444,120.11 L444,150.46 A354,354 0 0 0 150.46,444 Z M556,120.11 A384,384 0 0 1 879.89,444 L849.54,444 A354,354 0 0 0 556,150.46 Z" />
      <path fill={ring} d="M952,500 L796.76,544 A300,300 0 0 0 796.76,456 Z M48,500 L203.24,456 A300,300 0 0 0 203.24,544 Z" />
      <path fill={GREEN} d="M500,38 L552,204.54 A300,300 0 0 0 448,204.54 Z M500,962 L448,795.46 A300,300 0 0 0 552,795.46 Z" />
      <path fill={GREEN} d="M691.63,707.18 L729.81,745.37 L745.37,729.81 L707.18,691.63 Z M292.82,691.63 L254.63,729.81 L270.19,745.37 L308.37,707.18 Z M308.37,292.82 L270.19,254.63 L254.63,270.19 L292.82,308.37 Z M707.18,308.37 L745.37,270.19 L729.81,254.63 L691.63,292.82 Z" />
      <path fill={ring} d="M281.99,579.35 A232,232 0 0 1 682.82,357.17 L632.39,396.57 A168,168 0 0 0 342.13,557.46 Z M682.82,642.83 A232,232 0 0 1 424.47,719.36 L445.3,658.85 A168,168 0 0 0 632.39,603.43 Z" />
      <path fill={GREEN} d="M438.06,486.58 A98,98 0 0 0 445.14,526.71 L288,658 Z M450.29,537.51 A98,98 0 0 0 466.7,559.3 L306,690 Z M474.33,566.16 A98,98 0 0 0 504.09,582.66 L358,688 Z" />
      <circle cx="536" cy="490" r="84" fill={GREEN} />
      <g clipPath={`url(#${clipId})`}>
        <circle cx="610" cy="630" r="150" fill="none" stroke={ring} strokeWidth="11" />
        <circle cx="610" cy="630" r="172" fill="none" stroke={ring} strokeWidth="11" />
      </g>
    </svg>
  );
}
